import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { asyncFactory } from 'typescript-fsa-redux-thunk';

export interface Links {
    next?: {
        href: string;
    };
}

export interface Response<Data> {
    results: Data;
    _links?: Links;
}

export function defineFetchCollectionWithNextForNamespace<
    Data,
    Params,
    NS extends string,
    Name extends string
>(namespace: NS, name: Name, fetch: (params: Params) => Promise<Response<Data[]>>) {
    type State = {
        isLoading: boolean;
        isInitial: boolean;
        data?: Data[];
        _links?: Links;
    };

    type GlobalState = { [key in NS]: { [key in Name]: State } };

    const initialState: State = {
        isLoading: false,
        isInitial: true,
    };

    const getData = (state: GlobalState) => state[namespace][name].data;
    const getIsLoading = (state: GlobalState) => state[namespace][name].isLoading;
    const getIsInitial = (state: GlobalState) => state[namespace][name].isInitial;
    const getHasNext = (state: GlobalState) => {
        const localState = state[namespace][name];

        return !!localState._links && !!localState._links.next && !!localState._links.next.href;
    };
    const getNextHref = (state: GlobalState): string | undefined => {
        const localState = state[namespace][name];

        return (
            (!!localState._links && !!localState._links.next && localState._links.next.href) ||
            undefined
        );
    };

    const createAction = actionCreatorFactory(namespace);
    const createAsyncAction = asyncFactory<GlobalState>(createAction);

    const fetchData = createAsyncAction<Params, Response<Data[]>>('FETCH', params => {
        return fetch(params);
    });

    const fetchNextData = createAsyncAction<Params, Response<Data[]>>(
        'FETCH_NEXT',
        (params, _, getState) => {
            const nextParams = (getNextHref(getState()) || params) as Params;

            return fetch(nextParams);
        },
    );

    const reducer = reducerWithInitialState<State>(initialState)
        .case(fetchData.async.started, state => ({
            ...state,
            isLoading: true,
            isInitial: false,
            data: undefined,
            _links: undefined,
        }))
        .case(fetchData.async.failed, state => ({
            ...state,
            isLoading: false,
        }))
        .case(fetchData.async.done, (state, { result }) => ({
            ...state,
            isLoading: false,
            data: [...(state.data || []), ...result.results],
            _links: result._links,
        }))
        .case(fetchNextData.async.started, state => ({
            ...state,
            isLoading: true,
            isInitial: false,
        }))
        .case(fetchNextData.async.failed, state => ({
            ...state,
            isLoading: false,
        }))
        .case(fetchNextData.async.done, (state, { result }) => ({
            ...state,
            isLoading: false,
            data: [...(state.data || []), ...result.results],
            _links: result._links,
        }));

    return {
        reducer,
        fetchData: fetchData.action,
        fetchNextData: fetchNextData.action,
        getData,
        getIsInitial,
        getIsLoading,
        getHasNext,
        getNextHref,
        getInitialState: (): State => initialState,
    };
}
